import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Search, AlertCircle, IndianRupee, ChevronRight, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../services/api';
import MonthlyResolveModal from './MonthlyResolveModal';

const toMonthStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

const MonthlyPaymentsTab = () => {
  const today = new Date();
  const [month, setMonth] = useState(toMonthStr(today));
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchSummary = useCallback(async (m) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.request(`/bookings/monthly-summary?month=${encodeURIComponent(m)}`);
      setData(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e?.message || 'Failed to load monthly summary');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(month); }, [month, fetchSummary]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((c) =>
      (c.customer_name || '').toLowerCase().includes(q) ||
      (c.customer_phone || '').includes(search)
    );
  }, [data, search]);

  const totals = useMemo(() => ({
    customers: filtered.length,
    bookings: filtered.reduce((sum, c) => sum + (c.booking_count || 0), 0),
    pending: filtered.reduce((sum, c) => sum + Number(c.total_pending || 0), 0),
  }), [filtered]);

  // Generate last 6 months including current + next 1 for selection
  const monthOptions = useMemo(() => {
    const opts = [];
    const base = new Date();
    base.setDate(1);
    for (let i = -5; i <= 1; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
      opts.push({
        value: toMonthStr(d),
        label: d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
      });
    }
    return opts.reverse();
  }, []);

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:p-6">
            <div className="text-lg sm:text-2xl font-bold text-gray-900">{totals.customers}</div>
            <div className="text-[10px] sm:text-sm text-gray-500">Customers</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-6">
            <div className="text-lg sm:text-2xl font-bold text-blue-600">{totals.bookings}</div>
            <div className="text-[10px] sm:text-sm text-gray-500">Monthly Bookings</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-6">
            <div className="text-lg sm:text-2xl font-bold text-amber-600">{fmt(totals.pending)}</div>
            <div className="text-[10px] sm:text-sm text-gray-500">Total Pending</div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base sm:text-xl flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Monthly Payments
            </CardTitle>
            <div className="flex gap-2 items-center">
              <select
                className="px-2 sm:px-3 py-1.5 border rounded-md text-xs sm:text-sm bg-white"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="monthly-payments-month-select"
              >
                {monthOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <Button
                variant="outline" size="sm"
                onClick={() => fetchSummary(month)} disabled={loading}
                data-testid="monthly-payments-refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-6 pt-0">
          <div className="relative mb-3">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <Input
              placeholder="Search by name or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="monthly-payments-search"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm p-3 mb-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500">
              <div className="font-medium text-gray-700 mb-1">
                No customers with pending monthly payments in this period.
              </div>
              <div className="text-xs text-gray-500">
                Only bookings where the admin marked <span className="font-mono">payment status = "monthly"</span> show up here.
                Try a different month, or create a booking with Payment Status “Monthly” from <span className="font-medium">Calendar → New Booking</span>.
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((c) => (
                <button
                  key={`${c.customer_phone}_${c.facility_id}`}
                  type="button"
                  onClick={() => setSelectedCustomer(c)}
                  className="w-full text-left rounded-lg border hover:border-blue-400 hover:shadow-sm bg-white px-3 py-3 flex items-center justify-between transition"
                  data-testid={`monthly-customer-row-${c.customer_phone}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 truncate">
                      {c.customer_name || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {c.customer_phone} · {c.facility_name || c.facility_id}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {c.booking_count} booking{c.booking_count !== 1 ? 's' : ''} ·{' '}
                      Paid {fmt(c.total_paid)} of {fmt(c.total_amount)}
                    </div>
                  </div>
                  <div className="text-right pl-3">
                    <div className="text-xs text-gray-500">Pending</div>
                    <div className="text-base sm:text-lg font-bold text-amber-600 flex items-center justify-end">
                      <IndianRupee className="h-4 w-4" />
                      {Number(c.total_pending).toFixed(2)}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 ml-2" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <MonthlyResolveModal
        open={!!selectedCustomer}
        customer={selectedCustomer}
        month={month}
        onClose={() => setSelectedCustomer(null)}
        onSettled={() => {
          setSelectedCustomer(null);
          fetchSummary(month);
        }}
      />
    </div>
  );
};

export default MonthlyPaymentsTab;
